import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_FETCH, API_POST } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';

// ========================================
// TYPES
// ========================================

export interface CutoffReminderPayload {
    siteCd: string;
    cutoffDay: number;
    cutoffTime: string; // "HH:mm:ss"
}

export interface CutoffReminderResponse {
    message: string;
    cutoffDay: number;
    cutoffTime: string;
    timestamp: string;
}

/** Shape returned by GET /api/gig/cutoffTime/{siteCd}. */
export interface CutoffReminder {
    siteCd: string;
    cutoffDay: number;
    /**
     * "HH:mm:ss", ALREADY in the site's local wall-clock time.
     * Consumers must render as-is — no Date/UTC conversion.
     */
    cutoffTime: string;
}

interface GigShiftSettingsState {
    saving: boolean;
    error: string | null;
    lastResponse: CutoffReminderResponse | null;

    loading: boolean;
    fetchError: string | null;
    /** null => not yet loaded OR site has no configured cutoff (404). */
    current: CutoffReminder | null;
}

const initialState: GigShiftSettingsState = {
    saving: false,
    error: null,
    lastResponse: null,
    loading: false,
    fetchError: null,
    current: null,
};

/** Rejection payload so the UI can distinguish 404 (no config yet) from real errors. */
interface FetchCutoffRejection {
    notFound: boolean;
    message: string;
}

// ========================================
// ASYNC THUNKS
// ========================================

/** GET /api/gig/cutoffTime/{siteCd} */
export const fetchCutoffReminder = createAsyncThunk<
    CutoffReminder,
    string,
    { rejectValue: FetchCutoffRejection }
>(
    'gigShiftSettings/fetchCutoffReminder',
    async (siteCd, { rejectWithValue }) => {
        try {
            const response = await API_FETCH(`${ENDPOINTS.GET_CUTOFF_TIME}/${encodeURIComponent(siteCd)}`);
            return response as CutoffReminder;
        } catch (error: any) {
            const status = error?.statusCode ?? error?.status ?? error?.response?.status;
            const message = error?.message || 'Failed to load cutoff reminder';
            console.error('❌ FetchCutoffReminder API Error:', error);
            return rejectWithValue({ notFound: status === 404, message });
        }
    }
);

/** POST /api/gigshift/submit-cutoff-time */
export const submitCutoffReminder = createAsyncThunk<
    CutoffReminderResponse,
    CutoffReminderPayload
>(
    'gigShiftSettings/submitCutoffReminder',
    async (payload, { rejectWithValue }) => {
        try {
            const response = await API_POST(ENDPOINTS.SUBMIT_CUTOFF_TIME, payload);
            return response as CutoffReminderResponse;
        } catch (error: any) {
            console.error('❌ SubmitCutoffReminder API Error:', error);
            return rejectWithValue(
                error?.data?.message || error?.message || 'Failed to save cutoff reminder'
            );
        }
    }
);

// ========================================
// SLICE
// ========================================
const gigShiftSettingsSlice = createSlice({
    name: 'gigShiftSettings',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
            state.fetchError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCutoffReminder.pending, (state) => {
                state.loading = true;
                state.fetchError = null;
            })
            .addCase(fetchCutoffReminder.fulfilled, (state, action) => {
                state.loading = false;
                state.current = action.payload;
            })
            .addCase(fetchCutoffReminder.rejected, (state, action) => {
                state.loading = false;
                // 404 => no cutoff configured for the site; not a real error.
                if (action.payload?.notFound) {
                    state.current = null;
                    state.fetchError = null;
                } else {
                    state.fetchError = action.payload?.message ?? 'Failed to load cutoff reminder';
                }
            })
            .addCase(submitCutoffReminder.pending, (state) => {
                state.saving = true;
                state.error = null;
            })
            .addCase(submitCutoffReminder.fulfilled, (state, action) => {
                state.saving = false;
                state.lastResponse = action.payload;
                // Keep local `current` in sync with what was just saved.
                state.current = {
                    siteCd: state.current?.siteCd ?? '',
                    cutoffDay: action.payload.cutoffDay,
                    cutoffTime: action.payload.cutoffTime,
                };
            })
            .addCase(submitCutoffReminder.rejected, (state, action) => {
                state.saving = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError } = gigShiftSettingsSlice.actions;
export default gigShiftSettingsSlice.reducer;